import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { SortDownIcon, SortUpIcon } from '../../shared/icons/Svgs';
import debounce from 'lodash.debounce';
import React, { useEffect } from 'react';
import { Pagination, Row, Col, Button, ButtonGroup } from 'react-bootstrap';
import Common from '../../shared/Common';
import type { IIncomeData } from '../../../interfaces/IIncomeData';
import type { Column, ColumnDef, ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';

import './style.css';

export interface IPaginationProps {
  pageIndex: number;
  pageSize: number;
  sortBy: SortingState;
  filters: ColumnFiltersState;
}

export interface ITableProps {
  data: IIncomeData[];
  loading: boolean;
  totalCount: number;
  totalPageCount: number;
  totalRevenue: number;
  fetchData: ({ pageIndex, pageSize, sortBy, filters }: IPaginationProps) => Promise<void>;
  fetchTrigger: number;
  showEditIncomeModal: (incomeId: number) => void;
}

export interface IDefaultColumnFilterProps {
  column: {
    filterValue: any;
    preFilteredRows: any;
    setFilter: any;
  };
}

function IncomesTable(props: ITableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const datetimeWithoutZoneConverter = (income: IIncomeData) => {
    if (income.time instanceof Date) {
      return income.time.toJSON().substring(0, 10);
    }
    return income.time?.substring(0, 10);
  };

  const columns: ColumnDef<IIncomeData>[] = React.useMemo<ColumnDef<IIncomeData>[]>(() => {
    const columns: ColumnDef<IIncomeData>[] = [];
    columns.push({
      header: () => <div>Data</div>,
      accessorFn: (row) => datetimeWithoutZoneConverter(row),
      id: 'time',
    });
    columns.push({
      header: () => <span>Opis</span>,
      accessorFn: (row) => row.description,
      id: 'description',
      enableSorting: false,
    });
    columns.push({
      header: () => <span>Kategoria</span>,
      accessorFn: (row) => row.categoryName,
      id: 'categoryName',
    });
    columns.push({
      header: () => <span>Miejsce</span>,
      accessorFn: (row) => row.location?.locationName,
      id: 'locationName',
    });
    columns.push({
      header: () => <span>Ile</span>,
      accessorFn: (row) => row.quantity + (row.quantityType ? ` ${row.quantityType?.incomeQuantityTypeShortName}` : ''),
      id: 'quantity',
      enableColumnFilter: false,
      enableSorting: false,
    });
    columns.push({
      header: () => <span>Przychód</span>,
      accessorFn: (row) => Common.Utils.getNumberWithSpaces((Math.round((row.revenue ?? 0) * 100) / 100).toFixed(2)),
      id: 'revenue',
      enableColumnFilter: false,
    });
    return columns;
  }, []);

  const table = useReactTable({
    data: props.data,
    columns: columns,
    pageCount: props.totalPageCount,
    state: {
      columnFilters,
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const debouncedFilterSearchChanged = React.useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>, column: Column<IIncomeData, unknown>) => {
        column.setFilterValue(e.target.value);
        table.setPageIndex(0);
      }, 500),
    [table]
  );

  const { fetchData, fetchTrigger } = props;

  useEffect(() => {
    fetchData({
      pageIndex,
      pageSize,
      sortBy: sorting,
      filters: columnFilters,
    });
  }, [fetchData, pageIndex, pageSize, sorting, columnFilters, fetchTrigger]);

  const items = [];
  for (
    let ind = 0,
      number = pageIndex < 2 ? 0 : pageIndex + 2 >= table.getPageCount() ? table.getPageCount() - 5 : pageIndex - 2;
    ind < 5;
    number++
  ) {
    if (number + 1 > table.getPageCount()) {
      break;
    }
    if (number + 1 <= 0) {
      continue;
    }
    items.push(
      <Pagination.Item key={number} active={number === pageIndex} onClick={() => table.setPageIndex(number)}>
        {number + 1}
      </Pagination.Item>
    );
    ind++;
  }
  return (
    <>
      <Row className='mb-1'>
        <Col>
          <Pagination>
            <Pagination.First onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} />
            <Pagination.Prev onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} />
            {items}
            <Pagination.Next onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} />
            <Pagination.Last
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            />
          </Pagination>
        </Col>
        <Col className='incomes-right-alignment'>
          <ButtonGroup aria-label='Dodawanie przychodów'>
            <Button
              variant='outline-dark'
              onClick={() => {
                props.showEditIncomeModal(0);
              }}
            >
              Dodaj przychód
            </Button>
          </ButtonGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className='table-responsive-sm'>
            <table id='incomes-table' className='table table-hover' aria-labelledby='tabelLabel'>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder ? null : (
                          <>
                            {header.column.getCanFilter() ? (
                              <div>
                                <input
                                  onChange={(e) => {
                                    debouncedFilterSearchChanged(e, header.column);
                                  }}
                                  placeholder={'Szukaj...'}
                                  className='no-border-input'
                                />
                              </div>
                            ) : null}
                            <div
                              {...{
                                style: header.column.getCanSort()
                                  ? {
                                      cursor: 'pointer',
                                    }
                                  : {},
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {{
                                asc: <SortUpIcon style={{ marginLeft: '5px' }} />,
                                desc: <SortDownIcon style={{ marginLeft: '5px' }} />,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {props.loading
                  ? Common.Ui.getSkeletonTableRows(10, table.getAllColumns().filter((c) => c.getIsVisible()).length, 35)
                  : table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => {
                          props.showEditIncomeModal(row.original.incomeId);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                        ))}
                      </tr>
                    ))}
                <tr>
                  {props.loading ? (
                    <td colSpan={10000}>Wczytywanie...</td>
                  ) : (
                    <td colSpan={10000}>
                      Razem {props.totalCount} przychodów
                      <span style={{ float: 'right' }}>
                        Zyskano w sumie{' '}
                        {Common.Utils.getNumberWithSpaces((Math.round(props.totalRevenue * 100) / 100).toFixed(2))} złotych
                      </span>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
    </>
  );
}

export default IncomesTable;
